import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, fmtMoney, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Send, CheckCircle2, X, AlertTriangle, ShieldCheck, Archive, ClipboardList, Trash2, RefreshCw } from "lucide-react";
import ManageDrafts from "@/components/ManageDrafts";

export default function PayrollRuns() {
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") === "drafts" ? "drafts" : "history");
  const [runs, setRuns] = useState([]);
  const [processing, setProcessing] = useState(null);
  const [preview, setPreview] = useState(null); // { run_id, ... } or null when dialog closed
  const [releasing, setReleasing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // run row pending delete confirmation
  const [deleting, setDeleting] = useState(false);
  const [retryTarget, setRetryTarget] = useState(null); // { run_id, employees, ... }
  const [retrying, setRetrying] = useState(null);

  // Show exactly who is being re-attempted before any money moves again.
  const openRetry = async (runId) => {
    try {
      const { data } = await api.get(`/payroll/runs/${runId}/failed-payouts`);
      if (!data.count) { toast.info("Nothing left to retry — every payout in this run landed."); load(); return; }
      setRetryTarget(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const confirmRetry = async () => {
    if (!retryTarget) return;
    const runId = retryTarget.run_id;
    setRetrying(runId);
    setRetryTarget(null);
    try {
      const { data } = await api.post(`/payroll/runs/${runId}/retry-failed`, {});
      const ok = (data.processed || []).length;
      const bad = (data.failed || []).length;
      if (ok) toast.success(`${ok} payout${ok === 1 ? "" : "s"} recovered · ${fmtMoney(data.total_usd || 0)}`);
      if (bad) toast.error(`${bad} payout${bad === 1 ? "" : "s"} still failing — check the Stripe dashboard`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setRetrying(null);
  };

  const confirmDeleteRun = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const { data } = await api.delete(`/payroll/runs/${deleteTarget.id}`);
      toast.success(`Run ${data.pay_date} deleted · ${data.deleted_stubs} stub${data.deleted_stubs === 1 ? "" : "s"} removed`, {
        description: `${data.restored_timesheets} timesheet row(s) restored to Approved — the hours are available again in New Payroll Run.`,
      });
      setDeleteTarget(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setDeleting(false);
  };

  const releaseRun = async (runId, isBackfill) => {
    const label = isBackfill
      ? "Release this backfill run to the portal? Employees will see the stubs but NO emails will be sent."
      : "Bulk approve & release every stub in this run to the employee portal + fire the pay-stub emails?";
    if (!window.confirm(label)) return;
    setReleasing(runId);
    try {
      const { data } = await api.post(`/payroll/runs/${runId}/release`, {});
      toast.success(`${data.newly_released} stub${data.newly_released === 1 ? "" : "s"} released${data.emails_queued ? " · emails queued" : " · emails skipped"}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setReleasing(null);
  };

  const load = useCallback(async () => {
    const { data } = await api.get("/payroll/runs");
    setRuns(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Step 1: fetch the preview so the confirmation dialog can show exact
  // amounts before any Stripe Transfers fire. Prevents accidental live batches.
  const openConfirm = async (runId) => {
    try {
      const { data } = await api.get(`/payroll/runs/${runId}/process/preview`);
      setPreview(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Step 2: actually fire the payouts. Only reached from inside the dialog.
  const confirmProcess = async () => {
    if (!preview) return;
    const runId = preview.run_id;
    setProcessing(runId);
    setPreview(null);
    try {
      const { data } = await api.post(`/payroll/runs/${runId}/process`, {});
      const okCount = (data.processed || []).length;
      const failCount = (data.failed || []).length;
      if (okCount) toast.success(`Processed ${okCount} direct-deposit payout${okCount === 1 ? "" : "s"} · ${fmtMoney(data.total_usd || 0)}`);
      if (failCount) toast.error(`${failCount} payout${failCount === 1 ? "" : "s"} failed — check Stripe dashboard`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setProcessing(null);
    }
  };

  return (
    <div className="p-10">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="overline mb-2">§ HISTORY</div>
          <h1 className="font-heading font-black text-5xl tracking-tight">Payroll Runs</h1>
        </div>
        <div className="flex items-center gap-2">
          <Link data-testid="new-run-btn" to="/app/payroll/new" className="hard-btn px-6 py-3 font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> New Run
          </Link>
        </div>
      </div>

      <div className="flex items-center gap-0 mb-8 border-b-2 border-ink" data-testid="payroll-tabs">
        {[
          { id: "history", label: "Run History", icon: Archive },
          { id: "drafts", label: "Manage Drafts", icon: ClipboardList },
        ].map((t) => (
          <button
            key={t.id}
            data-testid={`payroll-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-6 py-3 font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2 border-2 border-b-0 -mb-0.5 transition-colors ${tab === t.id ? "border-ink bg-ink text-white" : "border-transparent text-muted-foreground hover:text-ink"}`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "drafts" && <ManageDrafts />}

      {tab === "history" && (
      <div className="hard-card overflow-x-auto">
        <table className="w-full min-w-[900px] font-mono text-sm" data-testid="runs-table">
          <thead className="bg-ink text-white">
            <tr>
              <th className="text-left px-4 py-3 uppercase text-xs tracking-widest">Pay Date</th>
              <th className="text-left px-4 py-3 uppercase text-xs tracking-widest">Period</th>
              <th className="text-right px-4 py-3 uppercase text-xs tracking-widest">Gross</th>
              <th className="text-right px-4 py-3 uppercase text-xs tracking-widest">Taxes</th>
              <th className="text-right px-4 py-3 uppercase text-xs tracking-widest">Net</th>
              <th className="text-right px-4 py-3 uppercase text-xs tracking-widest">Actions</th>
            </tr>
          </thead>
          <tbody>
            {runs.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-muted-foreground">No payroll runs yet.</td></tr>}
            {runs.map((r, i) => (
              <tr key={r.id} className={i % 2 === 0 ? "bg-white" : "bg-[#F9F9F9]"} data-testid={`run-row-${r.id}`}>
                <td className="px-4 py-3 border-t border-ink/20 font-semibold">{r.pay_date}</td>
                <td className="px-4 py-3 border-t border-ink/20">{r.period_start} → {r.period_end}</td>
                <td className="px-4 py-3 border-t border-ink/20 text-right">{fmtMoney(r.totals?.gross)}</td>
                <td className="px-4 py-3 border-t border-ink/20 text-right text-bad">{fmtMoney(r.totals?.total_deductions)}</td>
                <td className="px-4 py-3 border-t border-ink/20 text-right font-black">{fmtMoney(r.totals?.net_pay)}</td>
                <td className="px-4 py-3 border-t border-ink/20 text-right">
                  <div className="inline-flex items-center gap-2 flex-wrap justify-end">
                    {r.is_backfill && (
                      <span data-testid={`run-backfill-${r.id}`} className="inline-flex items-center gap-1 border-2 border-ink bg-secondary px-2 py-1 text-[10px] uppercase tracking-widest">
                        <Archive className="w-3 h-3" /> Backfill
                      </span>
                    )}
                    {r.released_at ? (
                      <span data-testid={`run-released-${r.id}`} className="inline-flex items-center gap-1 border-2 border-good bg-good/10 text-good px-2 py-1 text-[10px] uppercase tracking-widest">
                        <ShieldCheck className="w-3 h-3" /> Released
                      </span>
                    ) : (
                      <button
                        type="button"
                        data-testid={`release-run-${r.id}`}
                        onClick={() => releaseRun(r.id, !!r.is_backfill)}
                        disabled={releasing === r.id}
                        className="border-2 border-ink bg-ink text-white px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-good inline-flex items-center gap-1 disabled:opacity-40"
                        title={r.is_backfill ? "Release to portal (no emails — backfill)" : "Release every stub to the employee portal + email"}
                      >
                        <ShieldCheck className="w-3 h-3" /> {releasing === r.id ? "Releasing…" : "Bulk Approve & Release"}
                      </button>
                    )}
                    {r.processed_at ? (
                      <span className="inline-flex items-center gap-1 border-2 border-good bg-good/10 text-good px-2 py-1 text-[10px] uppercase tracking-widest" data-testid={`run-processed-${r.id}`}>
                        <CheckCircle2 className="w-3 h-3" /> Processed
                      </span>
                    ) : (                      <button
                        type="button"
                        data-testid={`process-run-${r.id}`}
                        onClick={() => openConfirm(r.id)}
                        disabled={processing === r.id}
                        className="border-2 border-ink bg-white px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-ink hover:text-white inline-flex items-center gap-1 disabled:opacity-40"
                        title="Send ACH payouts via Stripe Connect"
                      >
                        <Send className="w-3 h-3" /> {processing === r.id ? "Sending…" : "Process Payroll"}
                      </button>
                    )}
                    {r.connect_failed_count > 0 && (
                      <button
                        type="button"
                        data-testid={`retry-failed-${r.id}`}
                        onClick={() => openRetry(r.id)}
                        disabled={retrying === r.id}
                        className="border-2 border-warn bg-warn/10 text-ink px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-warn inline-flex items-center gap-1 disabled:opacity-40"
                        title="Re-attempt only the employees whose payout failed"
                      >
                        <RefreshCw className="w-3 h-3" /> {retrying === r.id ? "Retrying…" : `Retry ${r.connect_failed_count} Failed`}
                      </button>
                    )}
                    <Link to="/app/paystubs" className="border border-ink px-2 py-1 text-xs uppercase tracking-widest hover:bg-ink hover:text-white">View Stubs</Link>
                    <button
                      type="button"
                      data-testid={`delete-run-${r.id}`}
                      onClick={() => setDeleteTarget(r)}
                      disabled={!!r.processed_at}
                      title={r.processed_at ? "Processed runs can't be deleted — funds already moved via Stripe" : "Delete this run and re-use its timesheets"}
                      className="border-2 border-bad bg-white text-bad px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-bad hover:text-white inline-flex items-center gap-1 disabled:opacity-30 disabled:hover:bg-white disabled:hover:text-bad"
                    >
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 bg-ink/60 flex items-center justify-center p-6" data-testid="delete-run-modal">
          <div className="bg-white border-2 border-ink w-full max-w-lg shadow-hard-lg p-8">
            <div className="overline mb-2">§ PERMANENT ACTION</div>
            <h2 className="font-heading font-black text-3xl mb-3">Are you sure?</h2>
            <p className="font-mono text-sm text-muted-foreground mb-4 leading-relaxed">
              This will delete the payroll run and allow you to re-run it with the same timesheets.
            </p>
            <div className="border-2 border-ink font-mono text-xs mb-4">
              <div className="flex justify-between px-3 py-2 border-b border-ink/20"><span>Pay date</span><b>{deleteTarget.pay_date}</b></div>
              <div className="flex justify-between px-3 py-2 border-b border-ink/20"><span>Period</span><b>{deleteTarget.period_start} → {deleteTarget.period_end}</b></div>
              <div className="flex justify-between px-3 py-2"><span>Net total</span><b>{fmtMoney(deleteTarget.totals?.net_pay)}</b></div>
            </div>
            <div className="border-2 border-warn bg-warn/10 p-3 mb-6 flex items-start gap-2 font-mono text-xs">
              <AlertTriangle className="w-4 h-4 text-warn shrink-0 mt-0.5" />
              <span>Every pay stub in this run is permanently removed (including released ones) and benefit accruals from the run are reversed. The period's timesheets return to <b>Approved</b> so the hours are immediately re-usable.</span>
            </div>
            <div className="flex items-center justify-between">
              <button onClick={() => setDeleteTarget(null)} data-testid="delete-run-cancel" className="px-6 py-3 border-2 border-ink font-mono uppercase text-xs tracking-widest hover:bg-ink hover:text-white">Cancel</button>
              <button onClick={confirmDeleteRun} disabled={deleting} data-testid="delete-run-confirm" className="px-6 py-3 border-2 border-bad bg-bad text-white font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2 disabled:opacity-50">
                <Trash2 className="w-4 h-4" /> {deleting ? "Deleting…" : "Delete Run"}
              </button>
            </div>
          </div>
        </div>
      )}

      {retryTarget && (
        <div className="fixed inset-0 z-50 bg-ink/60 flex items-center justify-center p-6" data-testid="retry-failed-modal">
          <div className="bg-white border-2 border-ink w-full max-w-lg shadow-hard-lg p-8">
            <div className="overline mb-2">§ RETRY FAILED PAYOUTS</div>
            <h2 className="font-heading font-black text-3xl mb-3">Re-attempt {retryTarget.count} payout{retryTarget.count === 1 ? "" : "s"}</h2>
            <p className="font-mono text-sm text-muted-foreground mb-4 leading-relaxed">
              Only the employees below are re-attempted. Anyone already paid in this run is untouched — nobody can be paid twice.
            </p>
            <div className="border-2 border-ink font-mono text-xs mb-4 max-h-56 overflow-y-auto">
              {retryTarget.employees.map((e) => (
                <div key={e.stub_id} className="flex justify-between px-3 py-2 border-b border-ink/20 last:border-b-0" data-testid={`retry-line-${e.stub_id}`}>
                  <span>{e.name}</span><b>{fmtMoney(e.amount_usd)}</b>
                </div>
              ))}
            </div>
            <div className="flex justify-between font-mono text-sm font-black mb-6">
              <span>Total re-attempt</span><span>{fmtMoney(retryTarget.total_usd)}</span>
            </div>
            <div className="flex items-center justify-between">
              <button onClick={() => setRetryTarget(null)} data-testid="retry-failed-cancel" className="px-6 py-3 border-2 border-ink font-mono uppercase text-xs tracking-widest hover:bg-ink hover:text-white">Cancel</button>
              <button onClick={confirmRetry} data-testid="retry-failed-confirm" className="hard-btn px-6 py-3 font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2">
                <RefreshCw className="w-4 h-4" /> Retry Payouts
              </button>
            </div>
          </div>
        </div>
      )}

      {preview && (
        <ProcessConfirmModal
          preview={preview}
          onCancel={() => setPreview(null)}
          onConfirm={confirmProcess}
        />
      )}
    </div>
  );
}

/**
 * Two-step confirmation: shows exact $ + employee count and blocks
 * confirmation entirely when Stripe Connect isn't ready or the run has
 * already been processed. Enter to confirm is deliberately NOT wired —
 * the admin must click.
 */
function ProcessConfirmModal({ preview, onCancel, onConfirm }) {
  const blocked = !preview.connect_ready || preview.already_processed || preview.count === 0;
  return (
    <div className="fixed inset-0 z-50 bg-ink/60 flex items-start justify-center p-8 overflow-y-auto" data-testid="process-confirm-modal">
      <div className="bg-white border-2 border-ink w-full max-w-xl hard-card-shadow">
        <div className="flex items-center justify-between p-4 border-b-2 border-ink bg-ink text-white">
          <div className="overline">§ CONFIRM PAYROLL PROCESSING</div>
          <button onClick={onCancel} className="hover:text-warn" aria-label="Close" data-testid="process-cancel-btn"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6">
          <div className="mb-3 flex items-baseline gap-3">
            <div className="font-heading font-black text-4xl tracking-tight" data-testid="process-total">
              {fmtMoney(preview.total_usd)}
            </div>
            <div className="font-mono text-sm text-muted-foreground">
              → <b data-testid="process-count">{preview.count}</b> employee{preview.count === 1 ? "" : "s"} · pay date <b>{preview.pay_date}</b>
            </div>
          </div>
          <p className="font-mono text-xs text-muted-foreground mb-4">
            This creates Stripe Connect Transfers to your business bank for every pending direct-deposit stub in this run. It cannot be undone from Checkmate Payroll — refunds must be issued from the Stripe dashboard.
          </p>

          {!preview.connect_ready && (
            <div className="border-2 border-warn bg-warn/10 p-3 mb-4 flex items-start gap-2" data-testid="process-not-ready">
              <AlertTriangle className="w-4 h-4 text-warn shrink-0 mt-0.5" />
              <div className="font-mono text-xs">
                Stripe Connect isn't fully verified yet — link and finish onboarding your business bank on the <Link className="underline" to="/app/billing">Billing page</Link> before processing.
              </div>
            </div>
          )}
          {preview.already_processed && (
            <div className="border-2 border-warn bg-warn/10 p-3 mb-4 font-mono text-xs" data-testid="process-already">
              This run was already processed. Re-processing is disabled to prevent double payouts.
            </div>
          )}
          {preview.count === 0 && (
            <div className="border-2 border-ink bg-secondary/60 p-3 mb-4 font-mono text-xs" data-testid="process-empty">
              No pending direct-deposit stubs in this run.
            </div>
          )}

          {preview.employees.length > 0 && (
            <div className="border-2 border-ink max-h-64 overflow-y-auto mb-5" data-testid="process-list">
              <table className="w-full font-mono text-xs">
                <thead className="bg-secondary/60">
                  <tr>
                    <th className="text-left px-3 py-2 uppercase tracking-widest text-[10px]">Employee</th>
                    <th className="text-left px-3 py-2 uppercase tracking-widest text-[10px]">Bank</th>
                    <th className="text-right px-3 py-2 uppercase tracking-widest text-[10px]">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.employees.map((e) => (
                    <tr key={e.stub_id} className="border-t border-ink/20" data-testid={`process-line-${e.stub_id}`}>
                      <td className="px-3 py-2">{e.name}</td>
                      <td className="px-3 py-2 text-muted-foreground">•••• {e.bank_last4 || "————"}</td>
                      <td className="px-3 py-2 text-right font-semibold">{fmtMoney(e.amount_usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="border-2 border-ink bg-white px-4 py-2 font-mono uppercase text-xs tracking-widest hover:bg-ink hover:text-white"
              data-testid="process-abort-btn"
            >Cancel</button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={blocked}
              className="hard-btn px-4 py-2 font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2 disabled:opacity-40"
              data-testid="process-confirm-btn"
            >
              <Send className="w-3 h-3" /> Send {fmtMoney(preview.total_usd)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
